import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Link as MuiLink
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getContactInfo } from '../utils/formHelpers';

const Contact = () => {
  const contactInfo = getContactInfo();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Contact Us
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
          Get in touch with The Reckoning Project team for general inquiries
        </Typography>
      </Box>

      {/* Contact Information */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EmailIcon sx={{ color: 'primary.main', mr: 2, fontSize: 30 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Email Contact
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                For general inquiries about The Reckoning Project:
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                {contactInfo.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                We aim to respond to general inquiries within 1-2 weeks.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationIcon sx={{ color: 'primary.main', mr: 2, fontSize: 30 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Mailing Address
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                For physical correspondence:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {contactInfo.mailingAddress.organization}<br />
                {contactInfo.mailingAddress.poBox}<br />
                {contactInfo.mailingAddress.streetAddress}<br />
                {contactInfo.mailingAddress.cityStateZip}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

  {/* Request a Questionnaire Call-to-Action */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 4,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Looking to Report Misconduct?
            </Typography>
            <Typography variant="body1">
              If you want to report misconduct, please use our dedicated questionnaire page with detailed submission options.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/submit-case"
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ 
              py: 1.5, 
              px: 3,
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            Request Questionnaire
          </Button>
        </Box>
      </Alert>

      {/* Contact Types */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            What to Contact Us About
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="General Inquiries"
                secondary="Questions about The Reckoning Project's mission, research, or methodology"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Media & Press"
                secondary="Interviews, press releases, or media collaboration requests"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Research Partnerships"
                secondary="Academic collaborations, data sharing, or research partnerships"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Technical Issues"
                secondary="Website problems, accessibility concerns, or technical feedback"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="body1">
          <strong>Please note:</strong> We are not a legal services organization and cannot provide 
          legal advice or representation. For case submissions about misconduct, please use our 
          <MuiLink component={Link} to="/submit-case" sx={{ mx: 1, fontWeight: 600 }}>
            Request Questionnaire
          </MuiLink>
          page instead of general email contact.
        </Typography>
      </Alert>

      {/* Response Information */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Response Times & Process
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="General Inquiries"
                secondary="We aim to respond to general questions and media requests within 1-2 weeks."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Research Collaborations"
                secondary="Partnership and research requests may take 1-2 weeks for initial response as they require team review."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Case Submissions"
                secondary="For misconduct reports, please use our Request a Questionnaire page. Case submissions receive acknowledgment within 1-2 weeks."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Contact;
